import type { ApolloError } from "@apollo/client";
import { Subject } from "rxjs";
import {
  type WorkspacePermissions,
  useWorkspaceStore,
  workspaceRefreshSubject
} from "stores/workspace/workspaceStore";

import type { Workspace } from "models/workspace";

/**
 * Shape preserved for backward compatibility with existing consumers.
 * Maps the Zustand store fields to the legacy WorkspaceContextState interface.
 */
export interface WorkspaceContextCompat {
  readonly workspaceId: Maybe<string>;
  readonly workspace: Maybe<Workspace>;
  readonly activeDashboardId: Maybe<string>;
  readonly activityBarWidth: number;
  readonly isActivityBarResizing: boolean;
  readonly isLoading: boolean;
  readonly error: Maybe<ApolloError>;
  readonly permissions: Maybe<WorkspacePermissions>;
  readonly workspaceRefreshObserver: Subject<string>;
}

/**
 * @deprecated Use `useWorkspaceStore` with named selectors directly.
 *
 * This shim exists solely for backward compatibility during the Phase 6
 * migration. It reads every field from the Zustand workspace store and
 * re-exposes them under the legacy `WorkspaceContextState` shape.
 *
 * Removal deadline: end of Phase 7 cleanup.
 *
 * @example
 * // Before (legacy)
 * const { workspaceId, workspace } = useWorkspaceContext();
 *
 * // After (preferred)
 * import { useWorkspaceStore, selectWorkspaceId, selectWorkspace } from "stores/workspace/workspaceStore";
 * const workspaceId = useWorkspaceStore(selectWorkspaceId);
 * const workspace   = useWorkspaceStore(selectWorkspace);
 */
export function useWorkspaceContext(): WorkspaceContextCompat {
  const workspaceId = useWorkspaceStore((s) => s.workspaceId);
  const workspace = useWorkspaceStore((s) => s.workspace);
  const activeDashboardId = useWorkspaceStore((s) => s.activeDashboardId);
  const activityBarWidth = useWorkspaceStore((s) => s.activityBarWidth);
  const isActivityBarResizing = useWorkspaceStore((s) => s.isActivityBarResizing);
  const isLoading = useWorkspaceStore((s) => s.isLoading);
  const error = useWorkspaceStore((s) => s.error);
  const permissions = useWorkspaceStore((s) => s.permissions);

  return {
    workspaceId,
    workspace,
    activeDashboardId,
    activityBarWidth,
    isActivityBarResizing,
    isLoading,
    error,
    permissions,
    workspaceRefreshObserver: workspaceRefreshSubject
  };
}
